import { MapControl, ControlPosition } from "@vis.gl/react-google-maps";
import {FaRegMap, FaMapMarkerAlt, FaPlus, FaMinus, FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight, FaSearchPlus, FaSearchMinus} from "react-icons/fa";import { MdLocationOn, MdLocationOff } from "react-icons/md";

import { useRef } from "react";

const API_URL = import.meta.env.VITE_API_URL;

const FaRegMapOff = ({ size = 20, color = "currentColor", strokeWidth = 2 }) => {
    return (
        <div style={{ position: "relative", width: size, height: size }}>
            <FaRegMap size={size} color={color}/>

            <svg width={size} height={size} style={{position: "absolute", top: 0, left: 0, pointerEvents: "none"}}>
                <line x1="2" y1="2" x2={size - 2} y2={size - 2} stroke="#fff" strokeWidth={strokeWidth + 3} strokeLinecap="round"/>
                <line x1="2" y1="2" x2={size - 2} y2={size - 2} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"/>
            </svg>
        </div>
    );
};

const MapButtonsControl = ({mapState, onTogglePOI, onToggleNeighborhood, usesMapId, variant = "inside", controller = null}) => {
    const showNeighborhood = usesMapId ? !mapState.neighborhoodStreets : mapState.neighborhoodStreets;
    const buttonStyle = "w-[40px] h-[40px] cursor-pointer hover:text-gray-900 bg-white shadow flex items-center justify-center";
    const buttonStyleBorder = "w-[40px] h-[40px] cursor-pointer hover:text-gray-900 bg-white shadow flex items-center justify-center border-1 border-gray-300";
    const isHeatmap = controller?.mapMode === 'heatmap';

    const animationRef = useRef(null);
    const actionRef = useRef(null);
    const velocityRef = useRef(0);

    const handleMapAction = (action) => {
        if (!controller) return;

        controller.setViewState(prev => {
            const zoom = prev.zoom;
            let { lat, lng } = prev.center;

            let divisor;
            if (zoom < 5) divisor = 100;
            else if (zoom < 10) divisor = 1000;
            else if (zoom < 13) divisor = 20000;
            else if (zoom < 16) divisor = 40000;
            else if (zoom < 19) divisor = 80000;
            else if (zoom < 20) divisor = 160000;
            else divisor = 320000;

            const step = 1000 / (zoom * divisor);

            switch (action) {
                case "up": lat += step; break;
                case "down": lat -= step; break;
                case "right": lng += step; break;
                case "left": lng -= step; break;

                case "zoom-in": return {...prev, zoom: Math.min(21, zoom + 1)};
                case "zoom-out": return {...prev, zoom: Math.max(1, zoom - 1)};

                default: return prev;
            }

            return {
                ...prev,
                center: { lat, lng }
            };
        });
    };

    const animate = () => {
        if (!actionRef.current) return;

        velocityRef.current = Math.min(velocityRef.current + 0.02, 1);

        controller.setViewState(prev => {
            const zoom = prev.zoom;
            let { lat, lng } = prev.center;

            const baseStep = 0.04;
            const speedMultiplier = 1 + velocityRef.current * 4;

            let zoomFactor = Math.pow(0.35, zoom - 10);
            if (zoom <= 10) zoomFactor = 1;

            let step = baseStep * speedMultiplier * zoomFactor;

            step = Math.min(step, 0.04);
            step = Math.max(step, 0.000005);


            switch (actionRef.current) {
                case "up": lat += step; break;
                case "down": lat -= step; break;
                case "right": lng += step; break;
                case "left": lng -= step; break;

                case "zoom-in": return { ...prev, zoom: Math.min(21, zoom + 0.05) };
                case "zoom-out": return { ...prev, zoom: Math.max(1, zoom - 0.05) };

                default: return prev;
            }

            return {
                ...prev,
                center: { lat, lng }
            };
        });

        animationRef.current = requestAnimationFrame(animate);
    };

    const startAction = (action) => {
        if (!controller) return;

        actionRef.current = action;
        velocityRef.current = 0;

        if (!animationRef.current) {
            animationRef.current = requestAnimationFrame(animate);
        }
    };

    const stopAction = () => {
        actionRef.current = null;
        velocityRef.current = 0;

        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
    };

    const bindHold = (action) => ({
        onMouseDown: () => startAction(action),
        onMouseUp: stopAction,
        onMouseLeave: stopAction,
        onTouchStart: () => startAction(action),
        onTouchEnd: stopAction,
        onClick: () => handleMapAction(action)
    });

    const Buttons = (
        <div className={`flex gap-2 text-gray-600 ${variant === "inside" ? "flex-col pr-2.5 pb-2" : "flex-row items-center"}`}>

            { onTogglePOI && (
                <button className={buttonStyle} onClick={onTogglePOI}>
                    {mapState.pointsOfInterest ? <MdLocationOn size={26}/> : <MdLocationOff size={26}/>}
                </button>
            )}

            { onToggleNeighborhood && (
                <button className={buttonStyle} onClick={onToggleNeighborhood}>
                    {showNeighborhood ? <FaRegMapOff size={20}/> : <FaRegMap size={20}/>}
                </button>
            )}

            { controller && (
                <>
                    <div className="flex items-center gap-2">

                        <div className="grid grid-cols-3 gap-1">
                            <div/>
                                <button className={buttonStyleBorder} {...bindHold("up")} title={'Mover para cima'}>
                                    <FaArrowUp size={12}/>
                                </button>
                            <div/>

                                <button className={buttonStyleBorder} {...bindHold("left")} title={'Mover para a esquerda'}>
                                    <FaArrowLeft size={12}/>
                                </button>
                            <div/>
                                <button className={buttonStyleBorder} {...bindHold("right")} title={'Mover para a direita'}>
                                    <FaArrowRight size={12}/>
                                </button>

                            <div/>
                                <button className={buttonStyleBorder} {...bindHold("down")} title={'Mover para baixo'}>
                                    <FaArrowDown size={12}/>
                                </button>
                            <div/>
                        </div>

                        <div className="flex flex-col gap-2 ml-[20px] items-center">
                            <button className={buttonStyleBorder} {...bindHold("zoom-in")} title={'Aumentar o zoom'}>
                                <FaSearchPlus size={14}/>
                            </button>

                            <button className={buttonStyleBorder} {...bindHold("zoom-out")} title={'Diminuir o zoom'}>
                                <FaSearchMinus size={14}/>
                            </button>
                        </div>
                    </div>

                    <button className={`${buttonStyleBorder} ml-[20px] mr-2`} onClick={() => controller.setMapMode(prev => prev === "heatmap" ? "marker" : "heatmap")} title={`${isHeatmap ? "Mudar para o mapa de pontos" : "Mudar para o mapa térmico"}`}>
                        {controller.mapMode === "heatmap" ? <FaMapMarkerAlt size={16} color={'#dc1f1f'}/> : <img src={`${API_URL}/public/assets/img/markers/iconThermalMap.svg`} className="w-[22px] h-[22px]" alt={'heatmap'}/>}
                    </button>

                    <button className={`${buttonStyleBorder} ${!isHeatmap ? "opacity-40 cursor-not-allowed" : ""}`} onClick={() => isHeatmap && controller.setRadius(r => Math.max(10, r - 10))} disabled={!isHeatmap}>
                        <FaMinus size={14}/>
                    </button>

                    <span className={`w-[40px] h-[40px] flex items-center border-1 border-gray-300 justify-center bg-white shadow text-sm font-bold cursor-default ${!isHeatmap ? 'text-[#ccc]' : ''}`}>
                        {controller.radius}
                    </span>

                    <button className={`${buttonStyleBorder} ${!isHeatmap ? "opacity-40 cursor-not-allowed" : ""}`} onClick={() => isHeatmap && controller.setRadius(r => Math.min(100, r + 10))} disabled={!isHeatmap}>
                        <FaPlus size={14}/>
                    </button>
                </>
            )}
        </div>
    );

    if (variant === "outside") {
        return <div className="flex gap-2 mb-2 print:hidden">{Buttons}</div>;
    }

    return (
        <MapControl position={ControlPosition.RIGHT_BOTTOM}>
            {Buttons}
        </MapControl>
    );
};

export default MapButtonsControl;